/**
 * Writes to the platform `dashboard_layouts` table — one envelope for every
 * builder (Layout Builder, Design Builder, published view, share).
 *
 * The platform commit reads `operation` from INSIDE `requestData` (and strips
 * it before writing the row); the envelope copy is what the transaction layer
 * reads. Sending only one of them rolls the write back while still answering
 * HTTP 200 — keep both, and keep them here so the builders cannot drift apart.
 */

export type DashboardLayoutsOperation = 'insert' | 'update' | 'upsert';

/** The platform envelope around one `dashboard_layouts` write. */
export function dashboardLayoutsEnvelope(
  operation: DashboardLayoutsOperation,
  requestData: Record<string, unknown>,
): Record<string, unknown> {
  return {
    requestData: { ...requestData, operation },
    operation,
    responseList: [],
    entity: 'dashboard_layouts',
    batchId: 'test',
    workflowId: 'ABC-123',
    endpoints: [],
    version: '1.0',
  };
}

/**
 * Publish a layout: update the caller's existing row (`uuid`) or insert a new one.
 * `forceUpdate` (an already-published dashboard's Update) sends `upsert` keyed on
 * `dashboard_id` + `email_id` (unique index uq_dashboard_layouts_dashboard_email):
 * the row is updated in place, or created if it is gone - never duplicated.
 */
export function buildPublishEnvelope(
  layoutJson: Record<string, unknown>,
  emailId: string,
  dashboardId: string,
  uuid: string | null = null,
  forceUpdate = false,
): Record<string, unknown> {
  const row = {
    email_id: emailId,
    role: 'User',
    layout_json: layoutJson,
    dashboard_id: dashboardId,
  };
  if (forceUpdate) return dashboardLayoutsEnvelope('upsert', { ...row, businessKey: ['dashboard_id', 'email_id'] });
  if (uuid) return dashboardLayoutsEnvelope('update', { ...row, uuid });
  return dashboardLayoutsEnvelope('insert', row);
}

/**
 * True when a write did not land: no response, or the platform's rolled-back
 * answer (HTTP 200 with `error` set, `commitStatus: COMMIT_ROLLBACK`).
 */
export function isPlatformCommitFailure(response: unknown): boolean {
  if (!response || typeof response !== 'object') return true;
  const { error, commitStatus } = response as { error?: unknown; commitStatus?: unknown };
  return !!error || commitStatus === 'COMMIT_ROLLBACK';
}
